/**
 * Custom hook for AI trip review generation
 * Handles OpenAI integration to generate structured trip reviews
 */

import { useState, useRef } from 'react';
import { PlaceItem } from '../../../types/explore';
import { calculateDistance } from '../../../utils/advancedTrip/placeUtils';

interface UseAIReviewParams {
  startLocation: { name: string; coordinates: [number, number] } | null;
  tripPlaces: PlaceItem[];
  apiBaseUrl: string;
}

export function useAIReview(params: UseAIReviewParams) {
  const { startLocation, tripPlaces, apiBaseUrl } = params;

  // State
  const [aiReview, setAiReview] = useState<string | null>(null);
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);

  // Ref
  const aiReviewRef = useRef<HTMLDivElement>(null);

  // Helper to check if place is in difficult terrain
  const isInCentralOrUvaProvince = (place: PlaceItem): boolean => {
    const city = (place.city || '').toLowerCase();
    const centralCities = ['kandy', 'matale', 'nuwara eliya'];
    const uvaCities = ['badulla', 'monaragala', 'ella', 'bandarawela', 'haputale'];
    return centralCities.some(c => city.includes(c)) ||
           uvaCities.some(c => city.includes(c));
  };

  // Build AI trip review prompt
  const buildTripReviewPrompt = () => {
    if (!startLocation || tripPlaces.length === 0) return '';

    const today = new Date();
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    const currentMonth = monthNames[today.getMonth()];
    
    // Calculate total visit time and travel time
    let totalVisitTime = 0; // in minutes
    let totalTravelTime = 0; // in minutes
    const travelSegments: string[] = [];
    
    // Calculate travel from start to first place
    if (tripPlaces.length > 0) {
      const firstPlace = tripPlaces[0];
      const lat1 = startLocation.coordinates[1];
      const lon1 = startLocation.coordinates[0];
      const lat2 = firstPlace.geo_point?.lat;
      const lon2 = firstPlace.geo_point?.lon || firstPlace.geo_point?.lng;
      
      if (lat2 && lon2) {
        let distance = calculateDistance(lat1, lon1, lat2, lon2);
        const inDifficultTerrain = isInCentralOrUvaProvince(firstPlace);
        distance *= inDifficultTerrain ? 1.5 : 1.2;
        const speed = inDifficultTerrain ? 25 : 30; // km/h
        const travelMinutes = Math.round((distance / speed) * 60);
        totalTravelTime += travelMinutes;
        travelSegments.push(`${startLocation.name} → ${firstPlace.title || firstPlace.name}: ${distance.toFixed(1)} km, ~${travelMinutes} min`);
      }
    }
    
    let prompt = `You are a travel expert reviewing a day trip itinerary. Today's date is ${today.toLocaleDateString()} (${currentMonth}). The traveler will start from ${startLocation.name} and visit the following places in order:\n\n`;
    
    tripPlaces.forEach((place, index) => {
      const placeType = place.type || place.entity_type;
      
      prompt += `${index + 1}. ${place.title || place.name}\n`;
      prompt += `   Type: ${placeType}\n`;
      
      if (place.rating) {
        prompt += `   Rating: ${place.rating}\n`;
      }
      
      // Calculate visit time for this place (skip hotels)
      let visitMinutes = 0;
      if (placeType === 'experience') {
        if (place.typical_time_spent) {
          prompt += `   Typical time spent: ${place.typical_time_spent}\n`;
          // Try to parse time from string like "2-3 hours" or "30 minutes"
          const timeMatch = place.typical_time_spent.match(/(\d+)/);
          if (timeMatch) {
            const timeValue = parseInt(timeMatch[1]);
            if (place.typical_time_spent.toLowerCase().includes('hour')) {
              visitMinutes = timeValue * 60;
            } else if (place.typical_time_spent.toLowerCase().includes('min')) {
              visitMinutes = timeValue;
            }
          }
        }
      } else if (placeType === 'attraction') {
        if (place.ideal_duration) {
          prompt += `   Ideal duration: ${place.ideal_duration}\n`;
          const timeMatch = place.ideal_duration.match(/(\d+)/);
          if (timeMatch) {
            const timeValue = parseInt(timeMatch[1]);
            if (place.ideal_duration.toLowerCase().includes('hour')) {
              visitMinutes = timeValue * 60;
            } else if (place.ideal_duration.toLowerCase().includes('min')) {
              visitMinutes = timeValue;
            }
          }
        }
      } else if (placeType === 'food') {
        visitMinutes = 60; // Default 1 hour for food
        prompt += `   Estimated dining time: 1 hour\n`;
      }
      // Skip hotels from time calculation
      
      if (placeType !== 'hotel') {
        totalVisitTime += visitMinutes;
      }
      
      // Type-specific fields
      if (placeType === 'experience') {
        if (place.best_time_of_day) prompt += `   Best time of day: ${Array.isArray(place.best_time_of_day) ? place.best_time_of_day.join(', ') : place.best_time_of_day}\n`;
        if (place.cultural_significance) prompt += `   Cultural significance: ${place.cultural_significance}\n`;
        if (place.seasonal_notes) prompt += `   Seasonal notes: ${Array.isArray(place.seasonal_notes) ? place.seasonal_notes.join(', ') : place.seasonal_notes}\n`;
        if (place.pros) prompt += `   Pros: ${Array.isArray(place.pros) ? place.pros.join(', ') : place.pros}\n`;
        if (place.cons) prompt += `   Cons: ${Array.isArray(place.cons) ? place.cons.join(', ') : place.cons}\n`;
      } else if (placeType === 'attraction') {
        if (place.best_time_of_day) prompt += `   Best time of day: ${Array.isArray(place.best_time_of_day) ? place.best_time_of_day.join(', ') : place.best_time_of_day}\n`;
        if (place.cultural_significance) prompt += `   Cultural significance: ${place.cultural_significance}\n`;
        if (place.seasonal_notes) prompt += `   Seasonal notes: ${Array.isArray(place.seasonal_notes) ? place.seasonal_notes.join(', ') : place.seasonal_notes}\n`;
        if (place.pros) prompt += `   Pros: ${Array.isArray(place.pros) ? place.pros.join(', ') : place.pros}\n`;
        if (place.cons) prompt += `   Cons: ${Array.isArray(place.cons) ? place.cons.join(', ') : place.cons}\n`;
      } else if (placeType === 'hotel') {
        if (place.amenities) prompt += `   Amenities: ${Array.isArray(place.amenities) ? place.amenities.join(', ') : place.amenities}\n`;
        if (place.location_description) prompt += `   Location: ${place.location_description}\n`;
        if (place.pros) prompt += `   Pros: ${Array.isArray(place.pros) ? place.pros.join(', ') : place.pros}\n`;
        if (place.cons) prompt += `   Cons: ${Array.isArray(place.cons) ? place.cons.join(', ') : place.cons}\n`;
      } else if (placeType === 'food') {
        if (place.signature_dishes) prompt += `   Signature dishes: ${Array.isArray(place.signature_dishes) ? place.signature_dishes.join(', ') : place.signature_dishes}\n`;
        if (place.food_description) prompt += `   Description: ${place.food_description}\n`;
        if (place.ambience) prompt += `   Ambience: ${place.ambience}\n`;
        if (place.pros) prompt += `   Pros: ${Array.isArray(place.pros) ? place.pros.join(', ') : place.pros}\n`;
        if (place.cons) prompt += `   Cons: ${Array.isArray(place.cons) ? place.cons.join(', ') : place.cons}\n`;
      }
      
      // Calculate travel to next place
      if (index < tripPlaces.length - 1) {
        const nextPlace = tripPlaces[index + 1];
        const lat1 = place.geo_point?.lat;
        const lon1 = place.geo_point?.lon || place.geo_point?.lng;
        const lat2 = nextPlace.geo_point?.lat;
        const lon2 = nextPlace.geo_point?.lon || nextPlace.geo_point?.lng;
        
        if (lat1 && lon1 && lat2 && lon2) {
          let distance = calculateDistance(lat1, lon1, lat2, lon2);
          const inDifficultTerrain = isInCentralOrUvaProvince(nextPlace);
          distance *= inDifficultTerrain ? 1.5 : 1.2;
          const speed = inDifficultTerrain ? 25 : 30; // km/h
          const travelMinutes = Math.round((distance / speed) * 60);
          totalTravelTime += travelMinutes;
          travelSegments.push(`${place.title || place.name} → ${nextPlace.title || nextPlace.name}: ${distance.toFixed(1)} km, ~${travelMinutes} min`);
        }
      }
      
      prompt += '\n';
    });
    
    // Add travel segments
    if (travelSegments.length > 0) {
      prompt += `\nTRAVEL SEGMENTS:\n`;
      travelSegments.forEach(segment => {
        prompt += `- ${segment}\n`;
      });
      prompt += '\n';
    }
    
    // Add time summary
    const totalHours = Math.floor((totalVisitTime + totalTravelTime) / 60);
    const totalMinutes = (totalVisitTime + totalTravelTime) % 60;
    const visitHours = Math.floor(totalVisitTime / 60);
    const visitMinutes = totalVisitTime % 60;
    const travelHours = Math.floor(totalTravelTime / 60);
    const travelMinutes = totalTravelTime % 60;
    
    prompt += `TIME ANALYSIS:
- Total visit time (at attractions/experiences/food): ${visitHours}h ${visitMinutes}m
- Total travel time: ${travelHours}h ${travelMinutes}m
- GRAND TOTAL: ${totalHours}h ${totalMinutes}m

`;
    
    prompt += `\nPlease provide a structured review of this day trip with the following sections:

**Quick Review**: A 2-3 sentence overview of the entire day trip, highlighting the best aspects and overall experience. IMPORTANTLY, analyze if this trip is feasible within a single day (typically 8-12 hours) based on the total time of ${totalHours}h ${totalMinutes}m. If it's too tight or rushed, mention this clearly.

**Pro Tips**: 3-4 actionable tips to make the most of this trip (timing, what to bring, booking advice, departure time suggestions, etc.)

**What to Look Out For**: 2-3 important warnings or things to be aware of (crowds, weather considerations, accessibility, time constraints, etc.)

Keep the response concise and practical. Consider the current month (${currentMonth}) when giving advice about seasonal factors.`;
    
    return prompt;
  };

  // Scroll to AI review section
  const scrollToReview = () => {
    setTimeout(() => {
      if (aiReviewRef.current) {
        aiReviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
  };

  // Handle finalize confirmation
  const handleFinalizeConfirm = async (
    setShowFinalizeModal: (show: boolean) => void,
    setIsTripFinalized: (finalized: boolean) => void
  ) => {
    setShowFinalizeModal(false);
    
    // Clear old review before generating new one
    setAiReview(null);
    
    // Set trip as finalized
    setIsTripFinalized(true);

    setIsGeneratingReview(true);
    
    try {
      const prompt = buildTripReviewPrompt();
      
      const response = await fetch(`${apiBaseUrl}/api/advanced-trip/generate-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to generate AI review');
      }

      const data = await response.json();
      const reviewText = data.review || 'No review generated';
      
      setAiReview(reviewText);
      scrollToReview();
      
    } catch (error) {
      alert(`Failed to generate AI review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingReview(false);
    }
  };

  // Generate AI review - show confirmation modal first
  const generateAIReview = (setShowFinalizeModal: (show: boolean) => void) => {
    if (tripPlaces.length === 0) {
      alert('Please add at least one place to your trip before generating a review.');
      return;
    }

    // Show confirmation modal
    setShowFinalizeModal(true);
  };

  return {
    aiReview,
    setAiReview,
    isGeneratingReview,
    aiReviewRef,
    generateAIReview,
    handleFinalizeConfirm
  };
}

